/**
 * Query key factory for proposals
 *
 * Provides hierarchical query keys for React Query cache management
 */

import type { ProposalListFilters } from '../core/types';

/**
 * Hierarchical query key factory for proposals
 *
 * Structure:
 * - ['proposals', orgId] - All proposals for an org
 * - ['proposals', orgId, 'list', filters?] - List query
 * - ['proposals', orgId, 'detail', proposalId] - Detail query
 *
 * @example
 * ```typescript
 * // Invalidate all proposals for org
 * queryClient.invalidateQueries({ queryKey: proposalQueryKeys.all(orgId) });
 *
 * // Invalidate all lists (keeps detail queries)
 * queryClient.invalidateQueries({ queryKey: proposalQueryKeys.lists(orgId) });
 *
 * // Invalidate specific detail
 * queryClient.invalidateQueries({ queryKey: proposalQueryKeys.detail(orgId, proposalId) });
 * ```
 */
export const proposalQueryKeys = {
  /**
   * All proposals for an organization
   */
  all: (orgId: number) => ['proposals', orgId] as const,

  /**
   * All list queries for an organization
   */
  lists: (orgId: number) => [...proposalQueryKeys.all(orgId), 'list'] as const,

  /**
   * Specific list query with filters
   */
  list: (orgId: number, filters?: ProposalListFilters) =>
    [...proposalQueryKeys.lists(orgId), filters] as const,

  /**
   * All detail queries for an organization
   */
  details: (orgId: number) =>
    [...proposalQueryKeys.all(orgId), 'detail'] as const,

  /**
   * Specific detail query for a proposal
   */
  detail: (orgId: number, proposalId: number) =>
    [...proposalQueryKeys.details(orgId), proposalId] as const,

  /**
   * All proposal detail queries (for legacy artifact endpoints)
   */
  proposalDetails: () => ['proposals', 'details'] as const,

  /**
   * Specific proposal detail query (for legacy artifact endpoint)
   */
  proposalDetail: (proposalId: number) =>
    [...proposalQueryKeys.proposalDetails(), proposalId] as const,

  /**
   * All proposal files queries for an organization
   */
  proposalFilesAll: (orgId: number) =>
    [...proposalQueryKeys.all(orgId), 'files'] as const,

  /**
   * Specific proposal files query
   */
  proposalFiles: (orgId: number, proposalId: number) =>
    [...proposalQueryKeys.proposalFilesAll(orgId), proposalId] as const,

  /**
   * All trigger queries for an organization
   */
  triggers: (orgId: number) =>
    [...proposalQueryKeys.all(orgId), 'trigger'] as const,

  /**
   * Specific trigger query for a proposal action
   */
  trigger: (orgId: number, proposalId: number, actionId: number) =>
    [...proposalQueryKeys.triggers(orgId), proposalId, actionId] as const,

  /**
   * All thread queries for an organization
   */
  threads: (orgId: number) =>
    [...proposalQueryKeys.all(orgId), 'thread'] as const,

  /**
   * Specific thread query for a proposal action
   */
  thread: (orgId: number, proposalId: number, actionId: number) =>
    [...proposalQueryKeys.threads(orgId), proposalId, actionId] as const,

  /**
   * All discussion-conversation detail queries for an org
   */
  discussionConversations: (orgId: number) =>
    [...proposalQueryKeys.all(orgId), 'discussionConversation'] as const,

  /**
   * Specific discussion-conversation detail query. `conversationId` is
   * nullable so callers can build a key before a conversation exists (e.g.
   * a disabled query); a non-numeric sentinel keeps that key distinct from
   * any real conversation id.
   */
  discussionConversation: (orgId: number, conversationId: number | null) =>
    [
      ...proposalQueryKeys.discussionConversations(orgId),
      conversationId ?? 'none',
    ] as const,

  /**
   * All LinkedIn outbound delivery-status queries for an organization
   */
  linkedinDeliveryStatuses: (orgId: number) =>
    [...proposalQueryKeys.all(orgId), 'linkedinDelivery'] as const,

  /**
   * Specific LinkedIn delivery-status query for a set of proposed-action ids.
   * `expectsDeliveryRows` is part of the key so approval starts a fresh
   * bootstrap window (see useLinkedInDeliveryStates).
   */
  linkedinDeliveryStatus: (
    orgId: number,
    ids: readonly number[],
    expectsDeliveryRows: boolean
  ) =>
    [
      ...proposalQueryKeys.linkedinDeliveryStatuses(orgId),
      ids,
      expectsDeliveryRows,
    ] as const,
};
